import {
  easeInOutSine,
  easeOutCubic,
} from "./uploadedConversationsVisualizerMotion"
import { clampNumber } from "./uploadedConversationsVisualizerParticles"
import { type SetSlice } from "./UploadedConversationsVisualizerPieBackground"

const UNDERLAY_FADE_OUT_END_PROGRESS = 0.14
const UNDERLAY_FADE_IN_START_PROGRESS = 0.8
const UNDERLAY_FADE_IN_END_PROGRESS = 0.95
const UNDERLAY_HOVER_RADIUS_MIN_PX = 15
const UNDERLAY_HOVER_RADIUS_PAD_SHARE = 0.72

export interface ClusterHoverZone {
  bucketKey: string
  centerX: number
  centerY: number
  radius: number
}

export interface ClusterUnderlayAggregate {
  maxRadius: number
  maxX: number
  maxY: number
  minX: number
  minY: number
}

export function resolveUnderlayTransitionAlpha(
  isTransitioning: boolean,
  transitionProgress: number,
) {
  if (!isTransitioning) {
    return 1
  }

  if (transitionProgress < UNDERLAY_FADE_OUT_END_PROGRESS) {
    const fadeOutProgress = easeOutCubic(
      clampNumber(transitionProgress / UNDERLAY_FADE_OUT_END_PROGRESS, 0, 1),
    )
    return 1 - fadeOutProgress
  }

  if (transitionProgress < UNDERLAY_FADE_IN_START_PROGRESS) {
    return 0
  }

  if (transitionProgress < UNDERLAY_FADE_IN_END_PROGRESS) {
    return easeInOutSine(
      clampNumber(
        (transitionProgress - UNDERLAY_FADE_IN_START_PROGRESS) /
          (UNDERLAY_FADE_IN_END_PROGRESS - UNDERLAY_FADE_IN_START_PROGRESS),
        0,
        1,
      ),
    )
  }

  return 1
}

export function upsertClusterUnderlayAggregate(
  aggregates: Map<number, ClusterUnderlayAggregate>,
  sliceIndex: number,
  pointX: number,
  pointY: number,
  radius: number,
) {
  const aggregate = aggregates.get(sliceIndex)
  if (aggregate) {
    aggregate.minX = Math.min(aggregate.minX, pointX)
    aggregate.maxX = Math.max(aggregate.maxX, pointX)
    aggregate.minY = Math.min(aggregate.minY, pointY)
    aggregate.maxY = Math.max(aggregate.maxY, pointY)
    aggregate.maxRadius = Math.max(aggregate.maxRadius, radius)
    return
  }

  aggregates.set(sliceIndex, {
    maxRadius: radius,
    maxX: pointX,
    maxY: pointY,
    minX: pointX,
    minY: pointY,
  })
}

export function buildClusterHoverZones(
  aggregates: Map<number, ClusterUnderlayAggregate>,
  slices: SetSlice[],
) {
  return Array.from(aggregates.entries(), ([sliceIndex, aggregate]) => {
    const bucketKey = slices[sliceIndex]?.bucket.key
    if (!bucketKey) {
      return null
    }

    const centerX = (aggregate.minX + aggregate.maxX) * 0.5
    const centerY = (aggregate.minY + aggregate.maxY) * 0.5
    const extentRadius =
      Math.max(
        (aggregate.maxX - aggregate.minX) * 0.5,
        (aggregate.maxY - aggregate.minY) * 0.5,
      ) +
      aggregate.maxRadius * UNDERLAY_HOVER_RADIUS_PAD_SHARE

    return {
      bucketKey,
      centerX,
      centerY,
      radius: Math.max(UNDERLAY_HOVER_RADIUS_MIN_PX, extentRadius),
    } satisfies ClusterHoverZone
  }).filter((hoverZone): hoverZone is ClusterHoverZone => hoverZone !== null)
}

export function resolveHoveredBucketKeyFromZones(
  hoverZones: ClusterHoverZone[],
  pointerX: number,
  pointerY: number,
) {
  let hoveredBucketKey: string | null = null
  let bestDistanceShare = Number.POSITIVE_INFINITY

  for (const hoverZone of hoverZones) {
    const distanceFromCenter = Math.hypot(
      pointerX - hoverZone.centerX,
      pointerY - hoverZone.centerY,
    )
    if (distanceFromCenter > hoverZone.radius) {
      continue
    }

    const distanceShare = distanceFromCenter / Math.max(hoverZone.radius, 1)
    if (distanceShare < bestDistanceShare) {
      bestDistanceShare = distanceShare
      hoveredBucketKey = hoverZone.bucketKey
    }
  }

  return hoveredBucketKey
}
